/* eslint-disable no-mixed-spaces-and-tabs */
import { useState } from "react";
import { motion } from "framer-motion";
import Feature from "proximiio-js-library/lib/models/feature";
import {
  LiaChevronCircleUpSolid,
  LiaChevronCircleDownSolid,
} from "react-icons/lia";
import { t } from "i18next";
import { PiXBold } from "react-icons/pi";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import PoiDetailsImage from "./PoiDetailsImage";
import PoiDetailsHeading from "./PoiDetailsHeading";
import PoiDetailsDescription from "./PoiDetailsDescription";
import PoiDetailsRoutes from "./PoiDetailsRoutes";
import PoiDetailsRouteToggle from "./PoiDetailsRouteToggle";
import PoiDetailsSteps from "./PoiDetailsSteps";
import PoiDetailsSpecs from "./PoiDetailsSpecs";
import QRCode from "./QRCode";

import useMapStore from "@/store/mapStore";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import PoiDetailsRouteProgress from "./PoiDetailsRouteProgress";
import StopsToggle from "./StopsToggle";

function PoiDetails() {
  const [closestParkingFeature] = useState({} as Feature);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const [showMore, setShowMore] = useState(false);
  const activeStop = useMapStore((state) => state.activeStop);
  const stops = useMapStore((state) => state.stops);
  let poi = useMapStore((state) => state.routeFinish);
  const navMode = useMapStore((state) => state.navMode);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const kioskScaling = useMapStore((state) => state.kioskScaling);

  if (stops[activeStop]?.feature) {
    poi = stops[activeStop].feature!;
  }

  const resetView = useMapStore((state) => state.resetView);
  const cancelRouting = useMapStore((state) => state.cancelRouting);
  const setHaveRoutePreview = useMapStore((state) => state.setHaveRoutePreview);
  const setGlobalSearchOpen = useMapStore((state) => state.setGlobalSearchOpen);

  const multipointEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_MULTIPOINT === "true" && !kioskMode;

  const onCloseHandle = () => {
    if (stops.length > 2 && multipointEnabled) {
      console.log("close stops");
      setHaveRoutePreview(false);
      cancelRouting();
      setGlobalSearchOpen(true);
      return;
    }
    if (haveRouteDetails && navMode === "mall") {
      console.log("close have route details");
      setHaveRoutePreview(false);
      cancelRouting();
      return;
    }
    //setIsLoading(true);
    setTimeout(() => {
      console.log("close handler");
      resetView();
    }, 100);
  };

  const isVacant = () => {
    return [
      "f916f3f4-247e-41a3-81ed-451f2a187915:26c4225a-19a5-4ee4-b3e5-6bbf8d899f50",
      "f916f3f4-247e-41a3-81ed-451f2a187915:1763c1d2-0366-47f6-8290-a829f317faa0",
    ].includes(poi.properties?.amenity);
  };

  if (isVacant()) {
    return (
      <>
        <motion.div
          key="details"
          id="poiDetails"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "fixed inset-x-0 bottom-0 z-10 rounded-xl lg:absolute lg:bottom-auto lg:top-10 lg:h-full lg:max-w-md lg:group-data-[dir=ltr]:left-10 lg:group-data-[dir=rtl]:right-10 kiosk:!left-[calc(5%)] kiosk:bottom-20 kiosk:top-auto kiosk:h-auto kiosk:max-w-[90%]",
            showMore && "h-full rounded-none",
            mobileMode && "z-[101] rounded-b-none",
            !kioskScaling &&
              "kiosk:!left-[2%] kiosk:w-[calc(64%)] kiosk:scale-150 kiosk:!transform-cpu kiosk:group-data-[dir=ltr]:origin-bottom-left kiosk:group-data-[dir=rtl]:origin-bottom-right",
          )}
        >
          <Button
            onClick={onCloseHandle}
            size="icon"
            variant="default"
            className={cn(
              "absolute top-5 z-10 h-8 w-8 text-lg group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5 lg:h-10 lg:w-10",
            )}
          >
            <PiXBold />
          </Button>
          <div
            className={cn(
              "relative min-h-40 rounded-2xl bg-white px-6 py-5 pt-14 lg:min-h-96 lg:px-10 lg:pt-20",
              mobileMode && "rounded-b-none",
              showMore && mobileMode && poi.properties?.images[0] && "pt-0",
              haveRouteDetails && "pt-5",
            )}
          >
            <PoiDetailsHeading showMore={showMore} />
          </div>
        </motion.div>
      </>
    );
  }

  return (
    <>
      <motion.div
        key="details"
        id="poiDetails"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "fixed inset-x-0 bottom-0 z-10 rounded-xl lg:absolute lg:bottom-auto lg:top-10 lg:h-full lg:max-w-md lg:group-data-[dir=ltr]:left-10 lg:group-data-[dir=rtl]:right-10 kiosk:!left-[calc(5%)] kiosk:bottom-20 kiosk:top-auto kiosk:h-auto kiosk:max-w-[90%]",
          showMore && "h-full rounded-none",
          mobileMode && "z-[101] rounded-b-none",
          !kioskScaling &&
            "kiosk:!left-[2%] kiosk:w-[calc(48%)] kiosk:scale-[2] kiosk:!transform-cpu kiosk:group-data-[dir=ltr]:origin-bottom-left kiosk:group-data-[dir=rtl]:origin-bottom-right",
        )}
        style={{
          //@ts-expect-error color
          "--custom-handle-bg": `var(--primary)`,
        }}
      >
        <OverlayScrollbarsComponent
          className={cn(
            "w-full overflow-y-auto overflow-x-hidden rounded-xl bg-white shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] lg:max-h-[calc(100%-80px)]",
            showMore && "h-full rounded-none",
            mobileMode && "min-h-[268px] rounded-b-none",
          )}
          options={{
            scrollbars: { autoHide: "move", autoHideDelay: 3000 },
            overflow: { x: "hidden" },
          }}
          defer
        >
          <Button
            onClick={onCloseHandle}
            size="icon"
            variant="default"
            className={cn(
              "absolute top-5 z-10 h-8 w-8 text-lg group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5 lg:h-10 lg:w-10",
            )}
          >
            <PiXBold />
          </Button>
          {!haveRouteDetails && navMode === "mall" && stops.length <= 2 && (
            <div
              className={cn(
                "absolute top-5 z-20 block cursor-pointer text-4xl text-primary hover:text-primary group-data-[dir=ltr]:left-5 group-data-[dir=rtl]:right-5 lg:hidden",
              )}
              onClick={() => setShowMore(!showMore)}
            >
              <p className={cn("flex items-center gap-1")}>
                {!showMore ? (
                  <LiaChevronCircleUpSolid />
                ) : (
                  <LiaChevronCircleDownSolid />
                )}
                <span className="text-sm">
                  {t(!showMore ? "showMore" : "showLess")}
                </span>
              </p>
            </div>
          )}
          <PoiDetailsImage showMore={showMore} />
          <StopsToggle />
          <div
            className={cn(
              "relative rounded-tr-2xl bg-white px-6 py-5 pt-20 lg:top-[-20px] lg:px-10 lg:pt-10",
              showMore && mobileMode && poi.properties?.images[0] && "pt-0",
              haveRouteDetails && "pt-5",
              mobileMode && navMode !== "mall" && "pt-16",
              mobileMode && stops.length > 2 && "pt-16",
            )}
          >
            <PoiDetailsHeading showMore={showMore} />
            <PoiDetailsSpecs />
            {haveRouteDetails && <PoiDetailsRouteProgress />}
            {!haveRouteDetails && <PoiDetailsDescription showMore={showMore} />}
            {/*!haveRouteDetails && <PoiDetailsLink showMore={showMore} />*/}
            {haveRouteDetails && <PoiDetailsSteps />}
            {haveRouteDetails && <PoiDetailsRouteToggle />}
            {/*<PoiDetailsParking
      closestParkingFeature={closestParkingFeature}
      onSet={setClosestParkingFeature}
      showMore={showMore}
    />*/}
            {!haveRouteDetails && (
              <PoiDetailsRoutes
                closestParkingFeature={closestParkingFeature}
                showMore={showMore}
                setShowMore={setShowMore}
              />
            )}
            {!mobileMode && (
              <QRCode size={180} className="mb-0 mt-5 !transform-none" />
            )}
          </div>
        </OverlayScrollbarsComponent>
      </motion.div>
    </>
  );
}
export default PoiDetails;
